// 테스트 표시·시연 조직(`0088`) — 입력 해석 · 초기화 가능 판정 · 지우는 표 목록 · 장부 한 줄은 인메모리·Supabase·RPC 가 이 한 벌을 따른다.

import { AdminDemoNotResettableError, AdminDemoSlugConfirmError } from "./errors";
import { parseNameText } from "../../lib/name-text";
import { COMPANY_NAME_RULE } from "../org/types";
import { normalizeOperatorEmail, type Parsed } from "./rules";
import type { AdminAuditRow, AdminDemoAuditValues, AdminDemoCreate, AdminDemoInput, AdminDemoReset, AdminOrgTestSave, AdminOrgTestValues, AdminSeedStep } from "./types";

// 소문자·숫자·하이픈 3~40자 · 앞뒤는 하이픈이 아니다(간편접수 주소 `/s/intake/{slug}` 에 그대로 들어간다).
export const DEMO_SLUG = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;

// 초기화가 그 조직에서 지우는 표 — 자식부터(외래키·보호 트리거 순서) · 마지막 org_demo_reset()(`0133`) 의 delete 순서와 같아야 한다(마이그레이션 테스트가 맞춰 본다).
export const DEMO_RESET_TABLES = [
  "public_page",
  "portfolio",
  "change_order_item",
  "change_order",
  "log_comment",
  "project_log",
  "project_photo",
  "project_message_read",
  "project_message",
  "customer_review",
  "customer_notify_log",
  "cron_notice",
  "project_phase",
  "as_request",
  "project",
  "contract_payment",
  "contract_snapshot",
  "contract_share",
  "contract_file",
  "contract",
  "quote_actual_cost",
  "quote_phase_plan",
  "quote_item",
  "quote_trade",
  "quote_share",
  "quote_snapshot",
  "quote_file",
  "quote_cost",
  "quote_draft",
  "quote",
  "customer_stage_event",
  "notify_log",
  "activity",
  "referral_link",
  "intake_notice",
  "customer",
] as const;
export type DemoResetTable = (typeof DEMO_RESET_TABLES)[number];

// 초기화가 남기는 org_id 표 — 조직 설정·멤버·요금제·장부·카탈로그(공종·공정·단가·템플릿) · 둘을 합치면 org_id 있는 표 전체다.
export const DEMO_KEEP_TABLES = [
  "member",
  "invite",
  "org_setting",
  "org_plan",
  "api_key",
  "admin_audit",
  "feedback",
  "org_profile",
  "terms_consent",
  "plan_interest",
  "usage_mail_log",
  // 해지 파기 장부(0115) — 조직 FK 없이 파기 뒤에도 남는 cron 장부.
  "org_purge_log",
  // 파기 진행 표시(0122) — 해지 조직만 쓰는 cron 표라 시연 초기화와 무관하다.
  "org_purge_cursor",
  "trade_template",
  "price_item",
  "phase",
  "trade",
  "catalog_brand",
  "catalog_submission",
  // 공정 표준 글 고친 기록·숫자 제안·찬반(0110) — 다른 회사가 보는 공개 기록이라 초기화가 지우지 않는다.
  "process_standard_article_revision",
  "process_standard_proposal",
  "process_standard_proposal_vote",
  // 공정 표준 Q&A(0112) — 다른 회사가 보는 공용 피드라 초기화가 지우지 않는다.
  "process_standard_question",
  "process_standard_answer",
  "process_standard_post_report",
] as const;

// 시연 만들기로 만든 테스트 조직만 초기화한다 — 진짜 조직에 테스트 표시를 켜도 demo_created_at 이 없어 안 된다.
export const isDemoResettable = (org: { is_test?: boolean | null; demo_created_at?: string | null }): boolean =>
  org.is_test === true && typeof org.demo_created_at === "string" && org.demo_created_at !== "";

export const assertDemoResettable = (org: { is_test?: boolean | null; demo_created_at?: string | null }): void => {
  if (!isDemoResettable(org)) throw new AdminDemoNotResettableError();
};

// 운영자가 직접 친 slug 가 그 조직 slug 와 같아야 한다(대소문자·앞뒤 공백 무시 — 유일성 규칙 lower(slug) 과 같다).
export const demoSlugMatches = (orgSlug: string, typed: string): boolean => typed.trim().toLowerCase() === orgSlug.trim().toLowerCase();

export const assertDemoSlugConfirm = (orgSlug: string, typed: string): void => {
  if (!demoSlugMatches(orgSlug, typed)) throw new AdminDemoSlugConfirmError();
};

// 주인 멤버 이름 — 이메일 앞부분(멤버가 설정에서 고친다).
export const demoOwnerName = (email: string): string => email.split("@")[0] ?? email;

// 시연 만들기 본문 — 이름(시공사 만들기와 같은 상호 검사) · slug(소문자로) · 주인 이메일(소문자로).
export const parseDemoInput = (body: Record<string, unknown> | null): Parsed<AdminDemoInput> => {
  if (!body) return { ok: false, error: "요청 본문이 없습니다" };
  const company = parseNameText(body.name, COMPANY_NAME_RULE);
  if (!company.ok) return company;
  const name = company.value;
  const slug = typeof body.slug === "string" ? body.slug.trim().toLowerCase() : "";
  if (!DEMO_SLUG.test(slug)) return { ok: false, error: "주소(slug)는 소문자·숫자·하이픈 3~40자이고 하이픈으로 시작·끝나지 않습니다" };
  const email = normalizeOperatorEmail(body.ownerEmail);
  if (!email.ok) return { ok: false, error: `주인 계정 ${email.error}` };
  return { ok: true, value: { name, slug, ownerEmail: email.value } };
};

// 초기화 본문 — 운영자가 친 slug(대조는 서비스·RPC 가 한다).
export const parseDemoResetInput = (body: Record<string, unknown> | null): Parsed<{ confirmSlug: string }> => {
  if (!body) return { ok: false, error: "요청 본문이 없습니다" };
  const typed = typeof body.confirmSlug === "string" ? body.confirmSlug.trim() : "";
  if (typed === "") return { ok: false, error: "확인용으로 조직 주소(slug)를 입력해 주세요" };
  return { ok: true, value: { confirmSlug: typed } };
};

export interface AdminOrgTestInput {
  is_test: boolean;
  before: Pick<AdminOrgTestValues, "is_test">;
}

// 테스트 표시 본문 — 새 값과 화면이 본 값(before).
export const parseOrgTestInput = (body: Record<string, unknown> | null): Parsed<AdminOrgTestInput> => {
  if (!body) return { ok: false, error: "요청 본문이 없습니다" };
  if (typeof body.is_test !== "boolean") return { ok: false, error: "is_test 는 true·false 입니다" };
  const before = body.before;
  if (!before || typeof before !== "object" || Array.isArray(before)) return { ok: false, error: "before 가 없습니다 — 새로고침한 뒤 다시 해 주세요" };
  const b = (before as Record<string, unknown>).is_test;
  if (typeof b !== "boolean") return { ok: false, error: "before 모양이 틀렸습니다 — 새로고침한 뒤 다시 해 주세요" };
  return { ok: true, value: { is_test: body.is_test, before: { is_test: b } } };
};

export const orgTestValuesOf = (org: { is_test?: boolean | null; demo_created_at?: string | null }): AdminOrgTestValues => ({
  is_test: org.is_test === true,
  demo: typeof org.demo_created_at === "string" && org.demo_created_at !== "",
});

// 시연 조직의 테스트 표시를 끄면 시연 표시도 영구히 지운다 — 다시 켜도 초기화할 수 없다(실사용으로 넘긴 데이터 보호).
export const nextOrgTestValues = (current: AdminOrgTestValues, isTest: boolean): AdminOrgTestValues => ({ is_test: isTest, demo: current.demo === true && isTest });

// 걸음 중 넣은 행 수 — 장부 after.seeded(화면 번들이 시드 빌더·node 해시에 닿지 않게 여기 둔다).
export const seededCountOf = (steps: readonly AdminSeedStep[]): number => steps.filter((s) => s.op === "insert").length;

export const demoAuditValuesOf = (input: { name: string; slug: string; ownerEmail: string; steps: readonly AdminSeedStep[] }): AdminDemoAuditValues => ({
  name: input.name,
  slug: input.slug,
  owner_email: input.ownerEmail,
  seeded: seededCountOf(input.steps),
});

// 테스트 표시 장부 한 줄 — RPC 가 쓰는 것과 같은 값(인메모리용).
export const orgTestAuditOf = (save: AdminOrgTestSave, createdAt: string): AdminAuditRow => ({
  id: save.auditId,
  created_at: createdAt,
  operator_id: save.operatorId,
  actor_email: save.actorEmail,
  action: "org.test.set",
  org_id: save.orgId,
  target_user_id: null,
  before: save.before,
  after: save.values,
});

// 시연 만들기·초기화 장부 한 줄 — 전은 없고 후는 앱이 만든 값.
export const demoAuditOf = (save: AdminDemoCreate | AdminDemoReset, action: "org.demo.create" | "org.demo.reset", createdAt: string): AdminAuditRow => ({
  id: save.auditId,
  created_at: createdAt,
  operator_id: save.operatorId,
  actor_email: save.actorEmail,
  action,
  org_id: save.orgId,
  target_user_id: null,
  before: null,
  after: save.after,
});
